// Pure helpers for the Discount codes screens (no JSX, so they are unit-tested
// with node:test). Field names follow the API DTOs of contract §3.8.1.
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { CODES_COPY } from "./discountCodes.copy";

dayjs.extend(utc);
dayjs.extend(timezone);

export const IST = "Asia/Kolkata";
export const CODES_PAGE_SIZE = 20;

export const CODE_STATUS = {
  SCHEDULED: "SCHEDULED",
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  ENDED: "ENDED",
  CANCELLED: "CANCELLED",
};

/** `ended_reason` of a code ShoUp ops switched off (Switch Off, D76). */
export const CODE_ENDED_REASON = {
  CANCELLED_BY_OPS: "CANCELLED_BY_OPS",
};

/** True for a code ShoUp switched off: only ShoUp can Run it again (D76). */
export function isSwitchedOffByShoup(row) {
  return (
    row?.status === CODE_STATUS.CANCELLED &&
    row?.ended_reason === CODE_ENDED_REASON.CANCELLED_BY_OPS
  );
}

/** Preset antd Tag colours, mirroring EVENT_STATUS_COLOR meanings. */
export const CODE_STATUS_COLOR = {
  ACTIVE: "success",
  PAUSED: "warning",
  SCHEDULED: "blue",
  ENDED: "default",
  CANCELLED: "default",
};

/** CANCELLED (switched off by ShoUp) is shown as Ended (D8). */
export const CODE_STATUS_LABEL = {
  SCHEDULED: "Scheduled",
  ACTIVE: "Active",
  PAUSED: "Paused",
  ENDED: "Ended",
  CANCELLED: "Ended",
};

/** List filter: four options; ENDED also returns CANCELLED rows (server side). */
export const CODE_STATUS_FILTER_OPTIONS = [
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "ACTIVE", label: "Active" },
  { value: "PAUSED", label: "Paused" },
  { value: "ENDED", label: "Ended" },
];

export const AUDIENCE = {
  EVERYONE: "EVERYONE",
  MEMBERS: "MEMBERS",
  NON_MEMBERS: "NON_MEMBERS",
};

export const AUDIENCE_LABEL = {
  EVERYONE: "Everyone",
  MEMBERS: "Members",
  NON_MEMBERS: "Non-members",
};

export const LIMITS_SCOPE_LABEL = {
  PER_EVENT: CODES_COPY.limitsPerEvent,
  CODE: CODES_COPY.limitsAcrossAll,
};

/** True only when the config says the global switch is on (fail closed). */
const isNil = (v) => v === null || v === undefined;

export function isCodesEnabled(config) {
  return config?.enabled === true;
}

export function formatRupees(amount) {
  return `₹${Number(amount ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/** `15% off` · `20% off, up to ₹300` · `₹200 off` */
export function formatDiscount(discount) {
  if (!discount) return "—";
  if (discount.type === "FLAT") return `${formatRupees(discount.value)} off`;
  const base = `${discount.value}% off`;
  return discount.max_per_ticket
    ? `${base}, up to ${formatRupees(discount.max_per_ticket)}`
    : base;
}

export function formatAudience(audience) {
  return AUDIENCE_LABEL[audience] ?? "—";
}

/** Formats an ISO date-time in IST. */
export function formatIst(iso, fmt = "D MMM") {
  if (!iso) return "";
  const d = dayjs(iso);
  if (!d.isValid()) return "";
  return d.tz(IST).format(fmt);
}

/** Events cell: one name, two names, or `{n} events` + muted line when all past. */
export function formatEventsCell(row) {
  const events = row?.events ?? [];
  let primary = "—";
  if (events.length === 1) primary = events[0].name;
  else if (events.length === 2)
    primary = `${events[0].name}, ${events[1].name}`;
  else if (events.length > 2) primary = `${events.length} events`;
  const muted =
    events.length > 0 && row.has_upcoming_events === false
      ? CODES_COPY.noUpcomingEvents
      : null;
  return { primary, muted };
}

/** True when uses are limited per event on a multi-event code. */
function isPerEventLimit(row) {
  return row?.limits_scope === "PER_EVENT" && (row?.events?.length ?? 0) > 1;
}

/** Uses cell: `38 of 100` · `38 used` + `100 per event` · `38 used`. */
export function formatUsesCell(row) {
  const used = row?.uses_count ?? 0;
  if (isNil(row?.total_uses)) return { primary: `${used} used`, muted: null };
  if (isPerEventLimit(row))
    return { primary: `${used} used`, muted: `${row.total_uses} per event` };
  return { primary: `${used} of ${row.total_uses}`, muted: null };
}

/** Uses tile on the detail page: `38 of 100`, or `38` when unlimited. */
export function formatUsesTile(detail) {
  const used = detail?.stats?.uses ?? 0;
  if (isNil(detail?.total_uses) || isPerEventLimit(detail)) return `${used}`;
  return `${used} of ${detail.total_uses}`;
}

/** Valid cell: `3 Nov – 7 Nov` · `till 30 Nov` · `ended 31 Jul`. */
export function formatValidCell(row) {
  if (!row) return "—";
  if (
    row.status === CODE_STATUS.ENDED ||
    row.status === CODE_STATUS.CANCELLED
  ) {
    return `ended ${formatIst(row.ended_at ?? row.expires_at)}`;
  }
  if (row.starts_at) {
    return `${formatIst(row.starts_at)} – ${formatIst(row.expires_at)}`;
  }
  return `till ${formatIst(row.expires_at)}`;
}

/** `15% off · Everyone · Sunday 10K · 2 uses per person · 3 Nov – 7 Nov` */
export function formatSummaryLine(detail) {
  if (!detail) return "";
  const perPerson = detail.uses_per_person;
  const parts = [
    formatDiscount(detail.discount),
    formatAudience(detail.audience),
    formatEventsCell(detail).primary,
    !isNil(perPerson)
      ? `${perPerson} ${perPerson === 1 ? "use" : "uses"} per person`
      : null,
    formatValidCell(detail),
  ];
  return parts.filter(Boolean).join(" · ");
}

/**
 * Which actions a code in this status offers (contract §3.8.5). A code ShoUp
 * switched off (`endedReason` CANCELLED_BY_OPS) can't be run again (D76).
 */
export function getCodeActions(status, endedReason) {
  const isEnded =
    status === CODE_STATUS.ENDED || status === CODE_STATUS.CANCELLED;
  return {
    canEdit: !isEnded,
    canPause: status === CODE_STATUS.ACTIVE || status === CODE_STATUS.SCHEDULED,
    canResume: status === CODE_STATUS.PAUSED,
    canRunAgain:
      (status === CODE_STATUS.ACTIVE ||
        status === CODE_STATUS.PAUSED ||
        isEnded) &&
      !isSwitchedOffByShoup({ status, ended_reason: endedReason }),
    canEnd: !isEnded,
    canCopy: true,
  };
}

/** List subtitle with the effective cap (or without it when the cap is off). */
export function getListSubtitle(policy) {
  const capOn =
    policy?.cap_enabled === true && !isNil(policy?.effective_max_total_percent);
  return `${CODES_COPY.listSubtitleBase} ${
    capOn
      ? CODES_COPY.listSubtitleCapOn(policy.effective_max_total_percent)
      : CODES_COPY.listSubtitleCapOff
  }`;
}

export function getKeepMembershipExtra(policy) {
  const capOn =
    policy?.cap_enabled === true && !isNil(policy?.effective_max_total_percent);
  return capOn
    ? CODES_COPY.keepMembershipExtraCapOn(policy.effective_max_total_percent)
    : CODES_COPY.keepMembershipExtraCapOff;
}

/**
 * Settings cap field rule: a whole number from 1 to the platform max, else
 * "Enter a cap from 1 to {platformMax}%" (ui-spec §1 settings.field.cap.err).
 * Returns the error text, or null when the value is valid.
 */
export function getCapError(value, platformMax) {
  const n = Number(value);
  const valid =
    !isNil(value) &&
    value !== "" &&
    Number.isInteger(n) &&
    n >= 1 &&
    n <= Number(platformMax);
  return valid ? null : CODES_COPY.settingsCapErr(platformMax);
}

/**
 * Highest cap the organiser may save (D78 R4, R19): `organiser_max_allowed`
 * (min of platform and ShoUp's ceiling) when the policy view sends it, else
 * the platform max (older BFF), else 100. Rounded down to a whole number so a
 * stored decimal never asks for an impossible cap.
 */
export function getAllowedCapMax(policy) {
  const raw = !isNil(policy?.organiser_max_allowed)
    ? policy.organiser_max_allowed
    : (policy?.platform_max_total_percent ?? 100);
  const n = Number(raw);
  return Number.isFinite(n) ? Math.floor(n) : 100;
}

/**
 * The ShoUp ceiling to show ("ShoUp has set your maximum to X%"), or null
 * when ShoUp has set none or it is not below the platform max (D78 R4).
 */
export function getShoupCeilingNote(policy) {
  if (isNil(policy?.shoup_max_total_percent)) return null;
  if (isNil(policy?.organiser_max_allowed)) return null;
  const allowed = getAllowedCapMax(policy);
  const platform = Number(policy?.platform_max_total_percent ?? 100);
  return allowed < platform ? CODES_COPY.settingsShoupCeiling(allowed) : null;
}

/** Settings save body: only the organiser's cap (unchanged by D78). */
export function buildPolicyBody(values) {
  return { max_total_percent: values?.max_total_percent };
}

/**
 * The portal manages event codes only (D78 R11): rows of another kind (e.g. a
 * class code ShoUp made for this vendor) are not shown. Rows without `kind`
 * (older APIs) are kept.
 */
export function isPortalCode(row) {
  return isNil(row?.kind) || row.kind === "EVENT";
}

export function filterPortalCodes(rows) {
  return (rows ?? []).filter(isPortalCode);
}

/** "Can be combined" is rendered only when more than one code fits a booking. */
export function canShowCombine(policy) {
  return Number(policy?.codes_per_cart ?? 1) > 1;
}

/** An event is past when its end (else start) is before now (D10). */
export function isEventPast(event, now = dayjs()) {
  const at = event?.endDateTime ?? event?.startDateTime;
  if (!at) return false;
  return dayjs(at).isBefore(now);
}

const NO_COMMUNITY = "__none__";
const communityKey = (event) => event?.communityId ?? NO_COMMUNITY;

/**
 * Builds the event picker options.
 * - `communityId` (root users with several communities) filters the list.
 * - After the first pick, events of another community are disabled (D43).
 */
export function buildEventOptions(
  events,
  { communityId = null, pickedIds = [], now = dayjs() } = {}
) {
  const list = (events ?? []).filter(
    (e) => !communityId || e.communityId === communityId
  );
  const firstPicked = (events ?? []).find((e) => pickedIds.includes(e.id));
  const pickedKey = firstPicked ? communityKey(firstPicked) : null;
  return list.map((e) => {
    const otherCommunity = pickedKey !== null && communityKey(e) !== pickedKey;
    const when = e.startDateTime ? formatIst(e.startDateTime, "ddd D MMM") : "";
    return {
      value: e.id,
      label: [e.name, when].filter(Boolean).join(" · "),
      isPast: isEventPast(e, now),
      otherCommunity,
      disabled: otherCommunity && !pickedIds.includes(e.id),
    };
  });
}

/**
 * The community the form is working with: the Community field (root users with
 * several communities), else the first picked event's, else the only one.
 */
export function resolveFormCommunityId({
  communityField,
  pickedIds = [],
  events = [],
  communities = [],
}) {
  if (communityField) return communityField;
  const first = events.find((e) => pickedIds.includes(e.id));
  if (first) return first.communityId ?? null;
  if (communities.length === 1) return communities[0].community_id;
  return null;
}

/**
 * Turns a picker value (wall-clock time shown as IST) into an ISO instant.
 * With `endOfDay`, a 23:59 time means 23:59:59 IST of that day (D12).
 */
export function toIstIso(value, { endOfDay = false } = {}) {
  if (!value) return null;
  let v = dayjs(value);
  if (!v.isValid()) return null;
  if (endOfDay && v.hour() === 23 && v.minute() === 59) v = v.second(59);
  return dayjs.tz(v.format("YYYY-MM-DDTHH:mm:ss"), IST).toISOString();
}

/** Turns an ISO instant into a picker value that shows the IST wall-clock time. */
export function fromIsoToPickerValue(iso) {
  if (!iso) return undefined;
  const d = dayjs(iso);
  if (!d.isValid()) return undefined;
  return dayjs(d.tz(IST).format("YYYY-MM-DDTHH:mm:ss"));
}

/** Default time shown when an expiry date is picked without a time (D12). */
export function getExpiryDefaultTime() {
  return dayjs().hour(23).minute(59).second(59).millisecond(0);
}

const RUN_AGAIN_DEFAULT_DAYS = 30;

/**
 * Run again form defaults: starts now (empty), expires at 23:59 IST after the
 * same length as the previous run (30 days when that is unknown).
 */
export function getRunAgainDefaults(row, now = new Date()) {
  const start = row?.starts_at ? dayjs(row.starts_at) : null;
  const end = row?.expires_at ? dayjs(row.expires_at) : null;
  const days =
    start?.isValid() && end?.isValid() && end.isAfter(start)
      ? Math.max(1, Math.ceil(end.diff(start, "day", true)))
      : RUN_AGAIN_DEFAULT_DAYS;
  const expires = fromIsoToPickerValue(dayjs(now).toISOString())
    .add(days, "day")
    .hour(23)
    .minute(59)
    .second(0)
    .millisecond(0);
  return { starts_at: undefined, expires_at: expires };
}

/** `run` body of a RUN_AGAIN action (contract §3.8.5): `expires_at`, optional `starts_at`. */
export function buildRunAgainRun(values) {
  const startsAt = toIstIso(values?.starts_at);
  return {
    expires_at: toIstIso(values?.expires_at, { endOfDay: true }),
    ...(startsAt ? { starts_at: startsAt } : {}),
  };
}

const emptyToNull = (v) => (isNil(v) || v === "" ? null : v);

/**
 * Builds the create/edit body (ui-spec §2.1.3). `community_id` is never sent:
 * core derives it from `event_ids` (contract §3.8.2).
 */
export function buildCodeBody(values, { hasMembershipPlans, showCombine }) {
  const isFlat = values.discount_type === "FLAT";
  const eventIds = values.event_ids ?? [];
  const note =
    typeof values.private_note === "string" ? values.private_note.trim() : "";
  return {
    code: String(values.code ?? "")
      .trim()
      .toUpperCase(),
    discount: isFlat
      ? { type: "FLAT", value: Number(values.flat_value), max_per_ticket: null }
      : {
          type: "PERCENT",
          value: Number(values.percent_value),
          max_per_ticket: emptyToNull(values.max_per_ticket),
        },
    audience: hasMembershipPlans
      ? (values.audience ?? AUDIENCE.EVERYONE)
      : AUDIENCE.EVERYONE,
    keep_membership_discount: hasMembershipPlans
      ? values.keep_membership_discount !== false
      : true,
    event_ids: eventIds,
    total_uses: emptyToNull(values.total_uses),
    uses_per_person: emptyToNull(values.uses_per_person) ?? 1,
    limits_scope:
      eventIds.length > 1 && values.limits_scope === "CODE"
        ? "CODE"
        : "PER_EVENT",
    max_discounted_tickets: emptyToNull(values.max_discounted_tickets),
    starts_at: toIstIso(values.starts_at),
    expires_at: toIstIso(values.expires_at, { endOfDay: true }),
    ...(showCombine ? { can_combine: values.can_combine === true } : {}),
    private_note: note || null,
  };
}

/** Create body: optional fields left empty are not sent. */
export function toCreateBody(body) {
  return Object.fromEntries(
    Object.entries(body).filter(([, v]) => v !== null && v !== undefined)
  );
}

/** Edit body: only the fields that changed (omitted = unchanged). */
export function diffCodeBody(initialBody, nextBody) {
  return Object.fromEntries(
    Object.entries(nextBody).filter(
      ([k, v]) => JSON.stringify(initialBody?.[k]) !== JSON.stringify(v)
    )
  );
}

/** Maps a CodeDetail to the form's values. */
export function mapDetailToFormValues(detail) {
  const isFlat = detail.discount?.type === "FLAT";
  return {
    community_id: detail.community_id ?? undefined,
    code: detail.code,
    discount_type: isFlat ? "FLAT" : "PERCENT",
    percent_value: isFlat ? undefined : detail.discount?.value,
    max_per_ticket: isFlat
      ? undefined
      : (detail.discount?.max_per_ticket ?? undefined),
    flat_value: isFlat ? detail.discount?.value : undefined,
    audience: detail.audience ?? AUDIENCE.EVERYONE,
    keep_membership_discount: detail.keep_membership_discount !== false,
    event_ids: (detail.events ?? []).map((e) => e.event_id),
    total_uses: detail.total_uses ?? undefined,
    uses_per_person: detail.uses_per_person ?? 1,
    limits_scope: detail.limits_scope === "CODE" ? "CODE" : "PER_EVENT",
    max_discounted_tickets: detail.max_discounted_tickets ?? undefined,
    starts_at: fromIsoToPickerValue(detail.starts_at),
    expires_at: fromIsoToPickerValue(detail.expires_at),
    can_combine: detail.can_combine === true,
    private_note: detail.private_note ?? "",
  };
}

/** Default values of a new code (optionally with an event pre-ticked). */
export function getNewCodeDefaults({ eventId, communityId } = {}) {
  return {
    community_id: communityId ?? undefined,
    discount_type: "PERCENT",
    audience: AUDIENCE.EVERYONE,
    keep_membership_discount: true,
    event_ids: eventId ? [eventId] : [],
    uses_per_person: 1,
    limits_scope: "PER_EVENT",
    can_combine: false,
  };
}

/**
 * Maps a save error to a form field (D-9). Returns `{ name, message }` or null.
 */
export function getCodeFieldError(errors, code) {
  const first = errors?.[0];
  if (!first) return null;
  if (first.code === "CODE_NAME_TAKEN")
    return { name: "code", message: CODES_COPY.fieldNameTaken(code) };
  if (first.code === "CODE_NAME_RESERVED")
    return { name: "code", message: CODES_COPY.fieldNameReserved };
  if (first.code === "INVALID_CODE_NAME")
    return { name: "code", message: CODES_COPY.fieldNameFormat };
  if (first.property === "event_ids" && first.message)
    return { name: "event_ids", message: first.message };
  return null;
}

export const HISTORY_FIELD_LABEL = {
  created: "Code created",
  status: "Status",
  code: "Code name",
  "discount.type": "Discount type",
  "discount.value": "Discount value",
  "discount.max_per_ticket": "Maximum per ticket",
  audience: "Who can use it",
  keep_membership_discount: "Members keep membership discount",
  events: "Events",
  total_uses: "Total uses",
  uses_per_person: "Uses per person",
  limits_scope: "Limits count",
  max_discounted_tickets: "Discounted tickets per order",
  starts_at: "Starts",
  expires_at: "Expires",
  can_combine: "Can be combined",
  private_note: "Private note",
  policy: "Total discount cap",
};

export function formatHistoryField(field) {
  return HISTORY_FIELD_LABEL[field] ?? field ?? "—";
}

/** Formats a typed history value the same way the form and list show it. */
export function formatHistoryValue(field, value) {
  if (value === null || value === undefined || value === "") {
    if (field === "total_uses" || field === "discount.max_per_ticket")
      return "No limit";
    if (field === "max_discounted_tickets") return "All";
    if (field === "starts_at") return "Now";
    return "—";
  }
  switch (field) {
    case "discount.type":
      return value === "FLAT" ? "₹ off" : "% off";
    case "discount.max_per_ticket":
      return formatRupees(value);
    case "audience":
      return formatAudience(value);
    case "limits_scope":
      return LIMITS_SCOPE_LABEL[value] ?? String(value);
    case "status":
      return CODE_STATUS_LABEL[value] ?? String(value);
    case "starts_at":
    case "expires_at":
      return formatIst(value, "D MMM YYYY, h:mm a");
    default:
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (Array.isArray(value)) return value.join(", ");
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
  }
}

/** Run picker options: newest first; the first one is the current run. */
export function buildRunOptions(runs) {
  return (runs ?? []).map((r, i) => ({
    value: r.run_id,
    label:
      i === 0
        ? `Run ${r.number} (current)`
        : `Run ${r.number} (${formatIst(r.starts_at)}${
            r.ended_at ? ` – ${formatIst(r.ended_at)}` : ""
          })`,
  }));
}
